/* =========================================================
   Cliente HTTP del portal.

   El access token vive en memoria, nunca en localStorage: si un
   script ajeno se cuela en la página, no encuentra nada que robar.
   El refresh viaja en una cookie httpOnly que el navegador maneja
   solo — por eso todas las llamadas van con `credentials: 'include'`.

   Ante un 401 se intenta refrescar una vez y se repite la petición.
   Si el refresco también falla, se avisa al resto de la aplicación
   para que cierre la sesión.
   ========================================================= */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Detalle del servidor, si lo hubo. */
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export const setAccessToken = (t: string | null) => {
  accessToken = t;
};
export const hasSession = () => accessToken !== null;

/** La aplicación registra aquí qué hacer cuando la sesión se pierde. */
export const onSessionExpired = (fn: () => void) => {
  onSessionLost = fn;
};

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

async function parse(res: Response) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function raw(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  // Un FormData trae su propio content-type con el separador que genera el
  // navegador; ponérselo a mano lo rompe.
  if (init.body && !headers.has('content-type') && !(init.body instanceof FormData)) {
    headers.set('content-type', 'application/json');
  }
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  return fetch(BASE + path, { ...init, headers, credentials: 'include' });
}

/** Pide un access token nuevo con la cookie de refresco.
 *
 *  Devuelve también el usuario, con su rol y su alcance recién leídos de
 *  la base: es la única fuente fiable tras una recarga. Lo que haya en el
 *  navegador es una copia que su dueño puede editar. */
export async function refreshSession(): Promise<SessionUser | null> {
  const res = await fetch(BASE + '/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) return null;
  const body = await parse(res);
  if (!body?.accessToken) return null;
  accessToken = body.accessToken;
  return (body.user as SessionUser) ?? null;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await raw(path, init);

  // un 401 puede ser solo un access token caducado: se refresca una vez
  if (res.status === 401 && accessToken && !path.startsWith('/api/auth/')) {
    if (await refreshSession()) {
      res = await raw(path, init);
    } else {
      accessToken = null;
      onSessionLost?.();
    }
  }

  const body = await parse(res);
  if (!res.ok) {
    const message =
      (body && typeof body === 'object' && 'error' in body && String(body.error)) ||
      `Error ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data == null ? undefined : JSON.stringify(data) }),
  patch: <T>(path: string, data: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(data) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /** Sube un archivo. Sin JSON de por medio: 15 MB en base64 serían 20. */
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),

  /** Trae los bytes de un archivo, con el token de sesión puesto. Es lo
   *  que permite que el Storage Account siga cerrado: nadie habla con él
   *  salvo el servidor, y el servidor comprueba el alcance cada vez. */
  bytes: async (path: string): Promise<Blob> => {
    let res = await raw(path);
    if (res.status === 401 && accessToken) {
      if (await refreshSession()) res = await raw(path);
      else { accessToken = null; onSessionLost?.(); }
    }
    if (!res.ok) {
      const body = await parse(res);
      const message =
        (body && typeof body === 'object' && 'error' in body && String(body.error)) ||
        `Error ${res.status}`;
      throw new ApiError(res.status, message, body);
    }
    return res.blob();
  },
};

/* ---------------- sesión ---------------- */

export interface SessionUser {
  id: string;
  FirstName: string;
  LastName: string;
  Email: string;
  role: string;
  orgIds: string[];
  siteIds: string[];
  /** Si puede editar los datos de alguna organización. Ver SessionUser. */
  puedeEditarOrg?: boolean;
  /** Cuenta creada por el alta masiva: entró con una contraseña temporal
   *  y no puede hacer nada hasta cambiarla. */
  mustChangePassword?: boolean;
}

export type LoginResult =
  | { kind: 'ok'; user: SessionUser }
  | { kind: 'totp' }
  | { kind: 'error'; message: string; retryAt?: string };

export async function signIn(email: string, password: string, totp?: string): Promise<LoginResult> {
  try {
    const body = await api.post<{
      accessToken?: string;
      totpRequired?: boolean;
      user?: SessionUser;
    }>('/api/auth/login', { email, password, ...(totp ? { totp } : {}) });

    if (body.totpRequired) return { kind: 'totp' };
    if (!body.accessToken || !body.user) {
      return { kind: 'error', message: 'Respuesta inesperada del servidor' };
    }
    accessToken = body.accessToken;
    return { kind: 'ok', user: body.user };
  } catch (err) {
    if (err instanceof ApiError) {
      const retryAt =
        err.detail && typeof err.detail === 'object' && 'retryAt' in err.detail
          ? String(err.detail.retryAt)
          : undefined;
      return { kind: 'error', message: err.message, retryAt };
    }
    return { kind: 'error', message: 'No se pudo contactar al servidor' };
  }
}

/* ---------------- importación ---------------- */

export interface ImportRowError { row: number; id: string; message: string }

export interface ImportSummary {
  dryRun: boolean;
  /** false = no se escribió nada, pasara lo que pasara con las cifras. */
  applied: boolean;
  created: number;
  updated: number;
  unchanged: number;
  errors: ImportRowError[];
}

/** El servidor acepta 1000 filas por envío; un maestro más grande se
 *  parte en tandas para que un fallo no obligue a rehacerlo entero. */
export const IMPORT_CHUNK = 1000;

export async function importRows(
  entity: 'orgs' | 'sites' | 'instructors' | 'assignments',
  rows: Record<string, string | boolean>[],
  dryRun: boolean,
): Promise<{ ok: true; summary: ImportSummary } | { ok: false; message: string }> {
  try {
    const summary = await api.post<ImportSummary>(`/api/import/${entity}`, { rows, dryRun });
    return { ok: true, summary };
  } catch (err) {
    return {
      ok: false,
      message: err instanceof ApiError ? err.message : 'No se pudo contactar al servidor',
    };
  }
}

export type ChangePasswordResult = { ok: true } | { ok: false; message: string };

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> {
  try {
    await api.post('/api/auth/change-password', { currentPassword, newPassword });
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      message: err instanceof ApiError ? err.message : 'No se pudo contactar al servidor',
    };
  }
}

/* ---------------- restablecer la contraseña ----------------

   Las tres son públicas: quien las necesita es precisamente quien no
   puede entrar, así que ninguna lleva sesión. */

/** ¿Puede el portal mandar correo?
 *
 *  La pantalla de acceso lo pregunta antes de ofrecer el enlace: si no hay
 *  correo, prometer que se lo mandamos sería mentirle. Ante un fallo de
 *  red se contesta que no, que es el lado seguro del error. */
export async function capacidadesAcceso(): Promise<{ correo: boolean }> {
  try {
    return await request<{ correo: boolean }>('/api/auth/capacidades');
  } catch {
    return { correo: false };
  }
}

/** Pide el enlace de restablecimiento.
 *
 *  El servidor contesta lo mismo exista o no la cuenta, así que aquí no
 *  hay nada que distinguir: `correo: false` significa que el portal no
 *  tiene correo configurado, nunca que ese correo no esté registrado. */
export type EnlacePedido =
  | { ok: true; correo: boolean }
  | { ok: false; message: string };

export async function pedirEnlace(email: string): Promise<EnlacePedido> {
  try {
    const r = await request<{ ok: true; correo: boolean }>('/api/auth/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return { ok: true, correo: r.correo };
  } catch (err) {
    // Un 429 aquí significa que pidió demasiados enlaces seguidos; el
    // mensaje del servidor ya dice que espere.
    return {
      ok: false,
      message: err instanceof ApiError ? err.message : 'No se pudo contactar al servidor',
    };
  }
}

export type EnlaceRevisado =
  | { ok: true; kind: 'reset' | 'invitacion'; email: string; nombre: string }
  | { ok: false; motivo: 'desconocido' | 'usado' | 'caducado' | 'red' };

/** ¿Sirve este enlace? Se pregunta antes de pintar el formulario, para no
 *  pedirle una contraseña dos veces a quien no va a poder guardarla. */
export async function revisarEnlace(token: string): Promise<EnlaceRevisado> {
  try {
    return await request<EnlaceRevisado>(
      `/api/auth/reset?token=${encodeURIComponent(token)}`);
  } catch (err) {
    const motivo = err instanceof ApiError
      && err.detail && typeof err.detail === 'object' && 'motivo' in err.detail
      ? String((err.detail as { motivo: unknown }).motivo)
      : 'red';
    return {
      ok: false,
      motivo: (['desconocido', 'usado', 'caducado'].includes(motivo)
        ? motivo : 'red') as 'desconocido' | 'usado' | 'caducado' | 'red',
    };
  }
}

export async function establecerPassword(
  token: string, password: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await request('/api/auth/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      message: err instanceof ApiError ? err.message : 'No se pudo contactar al servidor',
    };
  }
}

export async function signOut() {
  try {
    await api.post('/api/auth/logout');
  } catch {
    // cerrar sesión en el cliente no depende de que el servidor conteste
  }
  accessToken = null;
}
